/**
 * BoostSection
 *
 * Boost-video header (icon + label + switch + description), the stack of
 * BoostCards, the optional "draft / scheduled" info line and the aggregate
 * boost-price line. Mirrors Flutter `_boostVideoWidget` from
 * content_monetization_screen.dart.
 */

import React from 'react';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import { ANIMATION_CONSTANTS } from '@/presentation/constants/animationConstants';
import { AssetIcon } from '@/presentation/components/common/icon/AssetIcon';
import { ToggleSwitch } from '@/presentation/components/common/controls/ToggleSwitch';
import { MathUtils } from '@/domain/utils/mathUtils';
import { BoostCard } from './BoostCard';
import type { BoostEntity } from '@/domain/entities/boost/boostEntity';

interface BoostSectionProps {
  enabled: boolean;
  boosts: BoostEntity[];
  boostPriceCoins: number;
  isDraft: boolean;
  isScheduled: boolean;
  onChange: (value: boolean) => void;
  onSelectBoost: (id: string) => void;
  onCycleOption: (id: string, forward: boolean) => void;
}

export const BoostSection: React.FC<BoostSectionProps> = ({
  enabled,
  boosts,
  boostPriceCoins,
  isDraft,
  isScheduled,
  onChange,
  onSelectBoost,
  onCycleOption,
}) => {
  const { theme } = useTheme();
  const responsive = useResponsiveDesign();
  const { l10n } = useLocale();

  const aggregatePriceText = l10n.publish.coinsWillBeChargedFromWallet.replace(
    '{coins}',
    MathUtils.removeTrailingZero(boostPriceCoins)
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: responsive.spacing.xs }}>
        <AssetIcon
          path="/assets/images/boost.png"
          width={responsive.icon.small}
          height={responsive.icon.small}
          color={theme.disabled}
        />
        <span
          style={{
            flex: 1,
            fontSize: responsive.font.base,
            fontWeight: responsive.fontWeight.semibold,
            color: theme.text.primary,
          }}
        >
          {l10n.publish.boostVideoLabel}
        </span>
        <ToggleSwitch
          value={enabled}
          onChange={onChange}
          qa={{ id: 'monetization-boost-toggle', value: enabled ? 'on' : 'off' }}
        />
      </div>

      <div
        style={{
          fontSize: responsive.font.sm,
          color: theme.text.secondary,
          fontWeight: responsive.fontWeight.medium,
          marginTop: responsive.spacing.sm,
          whiteSpace: 'pre-line',
        }}
      >
        {l10n.publish.boostVideoDescription}
      </div>

      <div
        style={{
          opacity: enabled ? 1 : 0.5,
          transition: `opacity ${ANIMATION_CONSTANTS.duration.normal}ms ${ANIMATION_CONSTANTS.easing.easeInOut}`,
          display: 'flex',
          flexDirection: 'column',
          gap: responsive.spacing.lg,
          marginTop: responsive.spacing.lg,
        }}
      >
        {boosts.map((boost) => (
          <BoostCard
            key={boost.id}
            boost={boost}
            isSelected={boost.isSelected}
            isInteractive={enabled}
            onSelect={() => onSelectBoost(boost.id)}
            onCycleOption={(forward) => onCycleOption(boost.id, forward)}
          />
        ))}

        {isScheduled && (
          <div
            style={{
              fontSize: responsive.font.sm,
              color: theme.text.secondary,
              fontWeight: responsive.fontWeight.medium,
            }}
          >
            {l10n.publish.boostScheduledInfo}
          </div>
        )}

        {isDraft && (
          <div
            style={{
              fontSize: responsive.font.sm,
              color: theme.text.secondary,
              fontWeight: responsive.fontWeight.medium,
            }}
          >
            {l10n.publish.boostDraftInfo}
          </div>
        )}

        {enabled && (
          <div
            style={{
              fontSize: responsive.font.sm,
              color: theme.text.primary,
            }}
          >
            {aggregatePriceText}
          </div>
        )}
      </div>
    </div>
  );
};
